// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 63.24.0 Slice C — the solution evidence of a controlled-testing case (C1–C3):
 * the one solution the organisation proposes to test, its solution type chosen from
 * what the installed pack declares, the customer group, the technical statement
 * attached to it, and the security level it is held to.
 *
 * ## What is stored, and what is never stored
 *
 * Stored: the solution type CODE the person chose, the customer group as typed,
 * the id of the technical statement file, and the security-level LINK (a scheme id
 * and a tier code the installed framework declares). Nothing else.
 *
 * Never stored: a completeness answer, a "complete" or "incomplete" word, a verdict,
 * a missing-items list, or any label. Each is computed on read from the stored record
 * and the installed frameworks, so a pack change reaches the record on the next read.
 *
 * ## Where the options come from (R-46)
 *
 * The solution types are read from installed form templates that declare
 * `register_surface: controlled_testing_solution`, the same reader the personnel
 * register uses. The security levels are read from the classification schemes the
 * installed frameworks declare for the `information_system` kind. Nothing in `src/`
 * names a solution type, a level, an article or an instrument.
 *
 * ## What the platform does not decide
 *
 * It does not say a solution is approved, admitted, certified, issued, passed or
 * failed. "Complete" means only that the four things the record holds are present;
 * the authority decides the rest.
 */
/** Collection holding one document per case's solution record. `solution_id` is also `_id`. */
import { CONTROLLED_TESTING_SOLUTION_ROLE } from './controlled-testing-requirement-roles.js';
export const CONTROLLED_TESTING_SOLUTIONS_COLLECTION = 'grc_controlled_testing_solutions';
/** The `register_surface` an installed pack form declares to feed the solution vocabulary. */
export const CONTROLLED_TESTING_SOLUTION_REGISTER_SURFACE = 'controlled_testing_solution';
/** The field id the surface's template carries the solution type under. */
export const CONTROLLED_TESTING_SOLUTION_FIELDS = {
    solutionType: 'solution_type',
};
/** The evidence owner type the technical statement is held under: owned by the solution record. */
export const CONTROLLED_TESTING_SOLUTION_EVIDENCE_OWNER_TYPE = 'controlled_testing_solution_evidence';
/** The customer group is free text, bounded. */
export const CONTROLLED_TESTING_SOLUTION_CUSTOMER_GROUP_MAX = 1000;
/** The classification-scheme kind the security level is read from. */
export const CONTROLLED_TESTING_SECURITY_LEVEL_SCHEME_KIND = 'information_system';
/**
 * The pack requirement that states what a Fintech solution description must contain. It
 * is a platform reference to a requirement the installed pack declares, and the platform
 * reads only its article reference from it.
 */
/** The requirement the solution record answers, read by its role (C3). */
export const CONTROLLED_TESTING_SOLUTION_REQUIREMENT_ROLE = CONTROLLED_TESTING_SOLUTION_ROLE;
/** Error codes the solution routes return. Stable, machine-readable names. */
export const CONTROLLED_TESTING_SOLUTION_ERROR_CODES = {
    NOT_FOUND: 'SOLUTION_NOT_FOUND',
    ALREADY_RECORDED: 'SOLUTION_ALREADY_RECORDED',
    /** A solution type the installed pack does not declare for this framework. */
    SOLUTION_TYPE_NOT_DECLARED: 'SOLUTION_TYPE_NOT_DECLARED',
    /** A security level no installed framework declares. */
    SECURITY_LEVEL_NOT_DECLARED: 'SECURITY_LEVEL_NOT_DECLARED',
    /** The technical statement is not held for this solution record. Upload it against the record first. */
    EVIDENCE_NOT_OWNED: 'SOLUTION_EVIDENCE_NOT_OWNED',
};
//# sourceMappingURL=controlled-testing-solution.js.map